import React, { useEffect, useState } from 'react';
import { getAllPromotionStats, getAllPromotions } from '../services/api';
import { ImpressionStats, Promotion } from '../services/api';
import './AdminStatistics.css';

const AdminStatistics: React.FC = () => {
  const [stats, setStats] = useState<ImpressionStats[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, promotionsRes] = await Promise.all([
        getAllPromotionStats(),
        getAllPromotions(),
      ]);
      setStats(statsRes.data);
      setPromotions(promotionsRes.data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPromotionTitle = (promotionId: number) => {
    const promo = promotions.find(p => p.id === promotionId);
    return promo?.title || `Promotion #${promotionId}`;
  };

  const calculateCTR = (stats: ImpressionStats): number => {
    if (stats.total_impressions === 0) return 0;
    return parseFloat(((stats.clicks / stats.total_impressions) * 100).toFixed(2));
  };

  if (loading) {
    return <div className="loading">Loading statistics...</div>;
  }

  return (
    <div className="admin-statistics">
      <h2>Promotion Statistics</h2>
      
      {stats.length === 0 ? (
        <div className="empty-state">No statistics available yet.</div>
      ) : (
        <div className="stats-table-container">
          <table className="stats-table">
            <thead>
              <tr>
                <th>Promotion</th>
                <th>Total Impressions</th>
                <th>Unique Users</th>
                <th>Unique Societies</th>
                <th>Clicks</th>
                <th>CTR</th>
                <th>Story Views</th>
                <th>Story Completes</th>
                <th>Views</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((stat) => (
                <tr key={stat.promotion_id}>
                  <td className="promotion-title">{getPromotionTitle(stat.promotion_id)}</td>
                  <td>{stat.total_impressions.toLocaleString()}</td>
                  <td>{stat.unique_users.toLocaleString()}</td>
                  <td>{stat.unique_societies.toLocaleString()}</td>
                  <td>{stat.clicks.toLocaleString()}</td>
                  <td>
                    <span className={`ctr-badge ${calculateCTR(stat) > 2 ? 'high' : calculateCTR(stat) > 1 ? 'medium' : 'low'}`}>
                      {calculateCTR(stat).toFixed(2)}%
                    </span>
                  </td>
                  <td>{stat.story_views.toLocaleString()}</td>
                  <td>{stat.story_completes.toLocaleString()}</td>
                  <td>{stat.impressions_by_type.view.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="summary-cards">
        <div className="summary-card">
          <h3>Total Impressions</h3>
          <p className="summary-value">
            {stats.reduce((sum, s) => sum + s.total_impressions, 0).toLocaleString()}
          </p>
        </div>
        <div className="summary-card">
          <h3>Total Unique Users</h3>
          <p className="summary-value">
            {new Set(stats.flatMap(s => Array(s.unique_users).fill(s.promotion_id))).size}
          </p>
        </div>
        <div className="summary-card">
          <h3>Total Clicks</h3>
          <p className="summary-value">
            {stats.reduce((sum, s) => sum + s.clicks, 0).toLocaleString()}
          </p>
        </div>
        <div className="summary-card">
          <h3>Average CTR</h3>
          <p className="summary-value">
            {stats.length > 0
              ? (
                  stats.reduce((sum, s) => sum + calculateCTR(s), 0) / stats.length
                ).toFixed(2)
              : 0}
            %
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminStatistics;

